/**
 * Dashboard Customization Panel - Premium Liquid Crystal Design
 * ===============================================================
 * Slide-out panel for customizing dashboard widget visibility,
 * order, and layout. Apple-inspired design with drag-and-drop.
 */

import React, { useState } from 'react';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetDescription,
  SheetFooter 
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Settings2, ChevronUp, ChevronDown, RotateCcw, Eye, EyeOff,
  GripVertical, Sparkles, BarChart3, Users, TrendingUp, Target,
  AlertTriangle, Cloud, Trophy, DollarSign, MessageSquare, ShoppingBag
} from 'lucide-react';
import { useDashboardCustomization } from '@/contexts/DashboardCustomizationContext';
import { toast } from '@/utils/safeToast';

// Widget icon mapping
const WIDGET_ICONS = {
  'greeting': Sparkles,
  'revenue-forecast': TrendingUp,
  'pl-snapshot': DollarSign,
  'staff-performance': Users,
  'customer-sentiment': MessageSquare,
  'competitor-intelligence': ShoppingBag,
  'supplier-performance': ShoppingBag,
  'weather-impact': Cloud,
  'goal-tracker': Target,
  'business-concerns': AlertTriangle,
  'recent-wins': Trophy,
  'opportunities': Sparkles,
  // Insights tab widgets
  'health-score': BarChart3,
  'what-if': Target,
  'insights-revenue-forecast': TrendingUp,
  'insights-goals': Target,
  'insights-weather': Cloud,
  'insights-competitors': ShoppingBag,
  'insights-sentiment': MessageSquare,
  'insights-suppliers': ShoppingBag,
  'insights-staff-leaderboard': Users,
};

// Category color mapping - matches logical reading flow
const CATEGORY_COLORS = {
  core: { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-800' },
  positive: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  urgent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  actionable: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  financial: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
  performance: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  market: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  planning: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-[#4DA6FF]', border: 'border-cyan-200 dark:border-cyan-800' },
  insights: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-800' },
};

export function DashboardCustomizationPanel() {
  const {
    widgets,
    categories,
    toggleWidget,
    moveWidgetUp,
    moveWidgetDown,
    resetToDefault,
    isPanelOpen,
    setIsPanelOpen,
  } = useDashboardCustomization();

  const enabledCount = widgets.filter(w => w.enabled).length;

  const handleReset = () => {
    resetToDefault();
    toast.success('Dashboard reset to default layout');
  };

  return (
    <Sheet open={isPanelOpen} onOpenChange={setIsPanelOpen}>
      <SheetContent 
        side="right" 
        className="w-[400px] sm:w-[450px] bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-l border-gray-200/50 dark:border-gray-700/50"
        data-testid="dashboard-customization-panel"
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Settings2 className="w-5 h-5 text-white" />
            </div>
            Customize Dashboard
          </SheetTitle>
          <SheetDescription className="text-gray-500 dark:text-gray-400">
            Toggle widgets on/off and reorder them to create your perfect dashboard.
          </SheetDescription>
        </SheetHeader>

        {/* Stats */}
        <div className="flex items-center gap-3 mb-4 p-3 rounded-2xl bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-900/20 dark:to-purple-900/10 border border-violet-100 dark:border-violet-800/50">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
              {enabledCount} of {widgets.length} widgets visible
            </span>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-280px)] pr-4">
          <div className="space-y-2">
            {widgets.map((widget, index) => {
              const Icon = WIDGET_ICONS[widget.id] || BarChart3;
              const categoryColor = CATEGORY_COLORS[widget.category] || CATEGORY_COLORS.core;
              const categoryInfo = categories[widget.category] || { name: widget.category };
              
              return (
                <div
                  key={widget.id}
                  className={`
                    group relative p-3 rounded-2xl border transition-all duration-200
                    ${widget.enabled 
                      ? 'bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md' 
                      : 'bg-gray-50 dark:bg-gray-800/20 border-gray-100 dark:border-gray-800 opacity-60'
                    }
                  `}
                  data-testid={`widget-config-${widget.id}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Drag Handle / Order Controls */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => moveWidgetUp(widget.id)}
                        disabled={index === 0}
                        className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        aria-label="Move up"
                      >
                        <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                      <button
                        onClick={() => moveWidgetDown(widget.id)}
                        disabled={index === widgets.length - 1}
                        className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        aria-label="Move down"
                      >
                        <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    </div>

                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-xl ${categoryColor.bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${categoryColor.text}`} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${widget.enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                          {widget.name}
                        </span>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`text-[10px] px-1.5 py-0 ${categoryColor.bg} ${categoryColor.text} ${categoryColor.border}`}
                      >
                        {categoryInfo.name}
                      </Badge>
                    </div>

                    {/* Toggle */}
                    <Switch
                      checked={widget.enabled}
                      onCheckedChange={() => toggleWidget(widget.id)}
                      className="data-[state=checked]:bg-violet-500"
                      aria-label={`Toggle ${widget.name}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <Separator className="my-4" />

        <SheetFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1 gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Default
          </Button>
          <Button
            onClick={() => setIsPanelOpen(false)}
            className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white"
          >
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Dashboard Customization Trigger Button
 */
export function DashboardCustomizationTrigger({ className = '' }) {
  const { setIsPanelOpen } = useDashboardCustomization();

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setIsPanelOpen(true)}
      className={`gap-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border-white/30 dark:border-gray-700/30 hover:bg-white dark:hover:bg-gray-800 ${className}`}
      data-testid="customize-dashboard-btn"
    >
      <Settings2 className="w-4 h-4" />
      <span className="hidden sm:inline">Customize</span>
    </Button>
  );
}

export default DashboardCustomizationPanel;
